/**
 * Subsequence command scorer, vendored from Superhuman's `command-score`
 * (v0.5.0) -- the matcher cmdk uses by default. Vendored rather than
 * depended on because the upstream package is archived.
 *
 * Ported verbatim to TypeScript (the scoring constants and algorithm are
 * unchanged from upstream) with one addition borrowed from cmdk's fork:
 * an optional `aliases` argument, so a command's hidden `keywords`
 * participate in matching alongside its visible label.
 *
 * Returns a score in (0, 1]: `1` is a perfect contiguous match; lower
 * values are weaker (word jumps, gaps, transpositions); and `0` means
 * "not a match at all" -- the typed characters do not all appear, in
 * order. That `0` is the filter. Unlike a fuzzy matcher, a character
 * that isn't present is a hard reject, not a forgivable error: typing
 * "xtatus" will not match "status", and "variations" will not match
 * "Integrations".
 *
 * Upstream: https://github.com/superhuman/command-score
 *
 * MIT License. Copyright (c) 2016 Superhuman. The `aliases` parameter
 * follows the modification in cmdk (MIT, Copyright (c) Paco Coursey).
 */

// A continuous match of characters scores 1. A new match at the start
// of a word scores well (users type the starts of fragments); a match
// elsewhere is worse but still counts. A transposition is heavily
// penalized. Each skipped/missing character and case mismatch decays
// the score slightly.
const SCORE_CONTINUE_MATCH = 1;
const SCORE_SPACE_WORD_JUMP = 0.9;
const SCORE_NON_SPACE_WORD_JUMP = 0.8;
const SCORE_CHARACTER_JUMP = 0.3;
const SCORE_TRANSPOSITION = 0.1;
const PENALTY_SKIPPED = 0.999;
const PENALTY_CASE_MISMATCH = 0.9999;
const PENALTY_NOT_COMPLETE = 0.99;

// Scoring cost grows superlinearly in haystack length, so matches past
// this point are dropped; labels and keywords come first in the haystack.
const MAX_HAYSTACK_LENGTH = 1000;

const IS_GAP_REGEXP = /[\\/_+.#"@[({&]/;
const COUNT_GAPS_REGEXP = /[\\/_+.#"@[({&]/g;
const IS_SPACE_REGEXP = /[\s-]/;
const COUNT_SPACE_REGEXP = /[\s-]/g;

function commandScoreInner(
  string: string,
  abbreviation: string,
  lowerString: string,
  lowerAbbreviation: string,
  stringIndex: number,
  abbreviationIndex: number,
  memoizedResults: Record<string, number>,
): number {
  if (abbreviationIndex === abbreviation.length) {
    return stringIndex === string.length ? SCORE_CONTINUE_MATCH : PENALTY_NOT_COMPLETE;
  }

  const memoizeKey = `${stringIndex},${abbreviationIndex}`;
  if (memoizedResults[memoizeKey] !== undefined) {
    return memoizedResults[memoizeKey];
  }

  const abbreviationChar = lowerAbbreviation.charAt(abbreviationIndex);
  let index = lowerString.indexOf(abbreviationChar, stringIndex);
  let highScore = 0;

  let score: number;
  let transposedScore: number;
  let wordBreaks: RegExpMatchArray | null;
  let spaceBreaks: RegExpMatchArray | null;

  while (index >= 0) {
    score = commandScoreInner(
      string,
      abbreviation,
      lowerString,
      lowerAbbreviation,
      index + 1,
      abbreviationIndex + 1,
      memoizedResults,
    );
    if (score > highScore) {
      if (index === stringIndex) {
        score *= SCORE_CONTINUE_MATCH;
      } else if (IS_GAP_REGEXP.test(string.charAt(index - 1))) {
        score *= SCORE_NON_SPACE_WORD_JUMP;
        wordBreaks = string.slice(stringIndex, index - 1).match(COUNT_GAPS_REGEXP);
        if (wordBreaks && stringIndex > 0) {
          score *= PENALTY_SKIPPED ** wordBreaks.length;
        }
      } else if (IS_SPACE_REGEXP.test(string.charAt(index - 1))) {
        score *= SCORE_SPACE_WORD_JUMP;
        spaceBreaks = string.slice(stringIndex, index - 1).match(COUNT_SPACE_REGEXP);
        if (spaceBreaks && stringIndex > 0) {
          score *= PENALTY_SKIPPED ** spaceBreaks.length;
        }
      } else {
        score *= SCORE_CHARACTER_JUMP;
        if (stringIndex > 0) {
          score *= PENALTY_SKIPPED ** (index - stringIndex);
        }
      }

      if (string.charAt(index) !== abbreviation.charAt(abbreviationIndex)) {
        score *= PENALTY_CASE_MISMATCH;
      }
    }

    if (
      (score < SCORE_TRANSPOSITION &&
        lowerString.charAt(index - 1) === lowerAbbreviation.charAt(abbreviationIndex + 1)) ||
      // allow duplicate letters. Ref superhuman/command-score#7428
      (lowerAbbreviation.charAt(abbreviationIndex + 1) === lowerAbbreviation.charAt(abbreviationIndex) &&
        lowerString.charAt(index - 1) !== lowerAbbreviation.charAt(abbreviationIndex))
    ) {
      transposedScore = commandScoreInner(
        string,
        abbreviation,
        lowerString,
        lowerAbbreviation,
        index + 1,
        abbreviationIndex + 2,
        memoizedResults,
      );

      if (transposedScore * SCORE_TRANSPOSITION > score) {
        score = transposedScore * SCORE_TRANSPOSITION;
      }
    }

    if (score > highScore) {
      highScore = score;
    }

    index = lowerString.indexOf(abbreviationChar, index + 1);
  }

  memoizedResults[memoizeKey] = highScore;
  return highScore;
}

function formatInput(string: string): string {
  // Convert all valid space characters to a plain space so they match
  // each other (e.g. "Auto-Advance" matches a typed "Auto Advance").
  return string.toLowerCase().replace(COUNT_SPACE_REGEXP, ' ');
}

/**
 * Score how well `abbreviation` (the query) matches `string` (the
 * candidate text). `aliases` are extra strings (e.g. a command's
 * `keywords`) folded into the candidate so they also count toward a
 * match. Returns 0 when the query is not a subsequence of the
 * candidate -- i.e. not a match.
 */
export function commandScore(string: string, abbreviation: string, aliases?: string[]): number {
  const joined = aliases && aliases.length > 0 ? `${string} ${aliases.join(' ')}` : string;
  const haystack = joined.length > MAX_HAYSTACK_LENGTH ? joined.slice(0, MAX_HAYSTACK_LENGTH) : joined;
  return commandScoreInner(haystack, abbreviation, formatInput(haystack), formatInput(abbreviation), 0, 0, {});
}
